<script lang="ts">
  import { onMount } from "svelte";
  import type { Picker } from "./types";
  import type { DateTime } from "./lib/date-time";
  import { parse, format, inputChanges, type InputChanges } from "./lib/utils";
  import { SelectionRange } from "./lib/range";
  import { DatePickerTranslations } from "./DatePicker.i18n";
  import DatePicker from "./DatePicker.svelte";
  import Overlay from "./overlay/Overlay.svelte";
  import { derived, get, type Readable } from "svelte/store";

  let pickerOpenFrom = false;
  let pickerOpenTo = false;

  export let host: HTMLElement;
  export let inputFrom: HTMLInputElement;
  export let inputTo: HTMLInputElement;

  export let placeholder: string;
  export let language: "de" | "it" | "en";
  export let mode: "standalone" | "content";
  export let rangeMode: boolean;
  export let minStay: number;
  export let minDate: DateTime;
  export let overlay: "inline" | "modal";
  export let picker: Picker = "year";
  export let readonly: boolean = false;

  onMount(() => {
    /**
     * public api
     */
    host.dispatchEvent(new Event("load"));
  });

  const selection = new SelectionRange(minDate, minStay || 0);

  let initialized: boolean;
  let inputs: InputChanges;
  let currentValue: [string, string] | undefined;

  $: T = DatePickerTranslations(language);

  /**
   * public API
   */
  export function setValue(value: [string, string] | undefined) {
    if (!Array.isArray(value) && value != null && (value as any) != "") throw new Error(`Invalid value! value: '${value}' must be a array of dates.`);

    const [from, to] = Array.isArray(value) ? value : [];
    const [fromDate, toDate] = [parse(from), parse(to)];

    // update value - fix formatting changes
    currentValue = Array.isArray(value) ? [fromDate?.toISOString(), toDate?.toISOString()] : undefined;

    selection.set(fromDate, toDate);
    selection.update();
  }

  /**
   * public API
   */
  export function getValue() {
    return currentValue;
  }

  /**
   * public API
   */
  export function open() {
    pickerOpenFrom = true;
  }

  /**
   * public API
   */
  export function close() {
    pickerOpenFrom = false;
  }

  onMount(() => {
    if (mode === "content" && inputFrom && inputTo) {
      inputFrom.addEventListener("click", () => (pickerOpenFrom = true));
      inputTo.addEventListener("click", () => (pickerOpenTo = true));
    } else {
      host.addEventListener("click", () => (pickerOpenFrom = true));
    }

    if (inputFrom && inputTo) {
      inputFrom.autocomplete = "off";
      inputTo.autocomplete = "off";

      inputs = inputChanges(inputFrom, inputTo, language);
      inputs.subscribe(([from, to]) => {
        selection.set(from, to);
        selection.update();
      });
    }

    initialized = true;
    selection.update();
  });

  let oldFrom, oldTo;
  const changes = derived<Readable<[DateTime, DateTime]>, [DateTime, DateTime]>(
    selection.value,
    ([from, to], set) => {
      // filter changes if values did not change!
      // this fixes a issue caused by reset()
      if (from?.ticks !== oldFrom || to?.ticks !== oldTo) {
        oldFrom = from?.ticks;
        oldTo = to?.ticks;
        set([from, to]);
      }
    },
    [null, null]
  );
  changes.subscribe(([from, to]) => {
    if (initialized) {
      // range-mode 
      if (rangeMode && from?.ticks === to?.ticks) {
        to = null;
      }

      let changes = inputFrom && inputTo ? inputs.set(from, to) : {};

      const fromIso = from?.toISOString() || undefined;
      const toIso = to?.toISOString() || fromIso;
      const [currentFrom, currentTo] = currentValue || [];

      if (fromIso !== currentFrom || toIso !== currentTo) {
        currentValue = fromIso ? [fromIso, toIso] : undefined;
        host.dispatchEvent(new Event("input"));
        host.dispatchEvent(new Event("change"));
        host.dispatchEvent(new Event("blur"));
      }

      if (changes.from) {
        inputFrom.dispatchEvent(new Event("input", { bubbles: false }));
        inputFrom.dispatchEvent(new Event("change", { bubbles: false }));
        inputFrom.dispatchEvent(new Event("blur", { bubbles: false }));
      }
      if (changes.to) {
        inputTo.dispatchEvent(new Event("input", { bubbles: false }));
        inputTo.dispatchEvent(new Event("change", { bubbles: false }));
        inputTo.dispatchEvent(new Event("blur", { bubbles: false }));
      }

      pickerOpenFrom = false;
      pickerOpenTo = false;
    }
  });

  let rangeModeOld;
  $: if (rangeMode !== rangeModeOld) {
    rangeModeOld = rangeMode;
    selection.update();
  }

  let readonlyOld;
  $: if (readonly !== readonlyOld) {
    readonlyOld = readonly;
    if (inputFrom) inputFrom.readOnly = readonly;
    if (inputTo) inputTo.readOnly = readonly;
  }

  let label = derived(selection.value, ([fromDate, toDate], set) => {
    const from = format(fromDate, language);
    const to = format(toDate, language);
    if (!from && !to) {
      set(``);
    } else if ((from && !to) || from === to) {
      set(`${rangeMode ? T.get("label.from") : T.get("label.on")} ${from}`);
    } else {
      set(`${from} - ${to}`);
    }
  });

  function reset() {
    let [from, to] = get(selection.value);
    selection.set(from, to);
  }
</script>

{#if mode === "standalone"}
  <div>
    {#if $label}
      {$label}
    {:else}
      <div class="lrs-placeholder">{placeholder || ""} &nbsp;</div>
    {/if}
  </div>

  <Overlay {overlay} closeLabel={T.get("overlay.close")} input={host} bind:open={pickerOpenFrom} on:close={(_) => pickerOpenFrom && setTimeout(reset, 200)}>
    <DatePicker bind:picker {selection} {language} />
  </Overlay>
{:else}
  <Overlay {overlay} closeLabel={T.get("overlay.close")} input={inputFrom || host} bind:open={pickerOpenFrom} on:close={(_) => pickerOpenFrom && setTimeout(reset, 200)}>
    <DatePicker bind:picker {selection} {language} />
  </Overlay>

  <Overlay {overlay} closeLabel={T.get("overlay.close")} input={inputTo || host} bind:open={pickerOpenTo} on:close={(_) => pickerOpenTo && setTimeout(reset, 200)}>
    <DatePicker bind:picker {selection} {language} />
  </Overlay>
{/if}

<style lang="scss" global>
  .lrs-placeholder {
    opacity: 0.4;
  }
</style>
